import React, { useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import MapView from "@arcgis/core/views/MapView";
import WebMap from "@arcgis/core/WebMap";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Graphic from "@arcgis/core/Graphic";
import * as geometryJsonUtils from "@arcgis/core/geometry/support/jsonUtils";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import Color from "@arcgis/core/Color";

import LayerList from "@arcgis/core/widgets/LayerList";
import Legend from "@arcgis/core/widgets/Legend";
import BasemapGallery from "@arcgis/core/widgets/BasemapGallery";
import Basemap from "@arcgis/core/Basemap";
import TileLayer from "@arcgis/core/layers/TileLayer";
import Measurement from "@arcgis/core/widgets/Measurement";
import Print from "@arcgis/core/widgets/Print";
import Expand from "@arcgis/core/widgets/Expand";
import Home from "@arcgis/core/widgets/Home";
import ScaleBar from "@arcgis/core/widgets/ScaleBar";
import Search from "@arcgis/core/widgets/Search";
import Zoom from "@arcgis/core/widgets/Zoom";

import * as webMercatorUtils from "@arcgis/core/geometry/support/webMercatorUtils";
import Point from "@arcgis/core/geometry/Point";
import * as queryModule from "@arcgis/core/rest/query.js";
import Query from "@arcgis/core/rest/support/Query.js";
import PopupTemplate from "@arcgis/core/PopupTemplate";

import { ArcGISMapProps, ArcGISMapHandle } from "../../types/config";

import { drawWidget } from "./custom-widgets/Draw";
import { googleStreetView } from "./custom-widgets/GoogleStreetView";
import { installCompactCoordinate } from "./custom-widgets/Coordinate";
import streetViewIcon from "./custom-widgets/images/googleStreetIcon.png"

import config from "../../config/config.json";
import "./styles/Map.css"
import * as CSS from "csstype";

const MapDivStyle: CSS.Properties = { height: "100%", width: "100%", position: "relative" };

const ArcGISMap = forwardRef<ArcGISMapHandle, ArcGISMapProps>(({ itemId, onMapIdentify }, ref) => {
    const mapDiv = useRef<HTMLDivElement>(null);
    const webmapRef = useRef<WebMap | null>(null);
    const viewRef = useRef<MapView | null>(null);
    // const initialized = useRef(false);
    const highlightLayerRef = useRef<GraphicsLayer | null>(null);

    useEffect(() => {
        if (!mapDiv.current) return;

        const webmap = new WebMap({ portalItem: { id: itemId } });
        webmapRef.current = webmap;
        const view = new MapView({ container: mapDiv.current, map: webmap });
        view.when(() => { if (view.popup) (view.popup as any).autoOpenEnabled = false; });
        viewRef.current = view;

        const topRightRow = document.createElement("div");
        topRightRow.className = "custom-top-right-row";
        Object.assign(topRightRow.style, { display: "flex", gap: "8px", alignItems: "center", padding: "8px" });
        view.ui.add(topRightRow, { position: "top-right", index: 0 });

        const styleEl = document.createElement("style");
        styleEl.textContent = `
          .custom-top-right-row .expand-wrap { position: relative; }
          .custom-top-right-row .expand-wrap .esri-expand__panel {
            right: 0 !important; left: auto !important; top: calc(100% + 8px);
            max-height: min(60vh, calc(100% - 56px)); max-width: min(600px, calc(100vw - 32px));
            overflow: auto; box-sizing: border-box;
          }
          .custom-top-right-row .esri-widget--button { margin: 0; }
          .esri-ui, .esri-ui-corner, .esri-ui-top-right, .esri-ui-inner-container { overflow: visible !important; }
          .esri-layer-list, .esri-basemap-gallery { position: static !important; margin-top: 0 !important; }
          .esri-icon-layer-list, .esri-icon-basemap, .esri-icon-feature-layer, .esri-icon-radio-unchecked { position: static !important; top:auto !important; left:auto !important; }

          .exb-btn { width:32px; height:32px; border:1px solid #dcdcdc; border-radius:4px; background:#fff; cursor:pointer; display:grid; place-items:center; }
          .exb-btn svg { width:18px; height:18px; fill:#2b2b2b; }

          .exb-pop { z-index:5; border-radius:8px; box-shadow:0 8px 24px rgba(0,0,0,.15); padding:12px; }
          .exb-row { display:grid; grid-template-columns:auto 1fr auto; gap:8px; align-items:center; margin:8px 0; }
          .exb-sw { display:grid; grid-template-columns: repeat(10, 18px); gap:6px; margin:8px 0; }
          .exb-swatch { width:18px; height:18px; border-radius:50%; border:1px solid #ccc; cursor:pointer; }
          .exb-title { font-weight:600; margin-bottom:8px; display:flex; align-items:center; gap:8px; }
          .exb-prev { width:24px; height:24px; border-radius:50%; border:2px solid #2b2b2b; background:#0079c133; }

          .esri-button { padding:6px 10px; border:1px solid #dcdcdc; background:#fff; border-radius:4px; cursor:pointer; font-size:12px; }
          .esri-button:disabled { opacity:.5; cursor:default; }
        `;
        mapDiv.current.appendChild(styleEl);

        const wrap = () => {
            const w = document.createElement("div");
            w.className = "expand-wrap";
            topRightRow.appendChild(w);
            return w;
        };

        view.when(async () => {
            const hl = new GraphicsLayer({ listMode: "hide" });
            webmap.add(hl);
            highlightLayerRef.current = hl;

            const legend = new Legend({ view });
            const layerList = new LayerList({ view, visibilityAppearance: "checkbox" });

            // Create custom basemaps from config
            const basemapConfig = (config as any).basemapGallery?.basemaps || [];
            const customBasemaps: __esri.Basemap[] = [];

            // Map of thumbnail images
            const thumbnailMap: Record<string, string> = {
                'esristreetsbm.png': (await import('./images/esristreetsbm.png')).default,
                'esriimagerybm.png': (await import('./images/esriimagerybm.png')).default,
                'esritopobm.png': (await import('./images/esritopobm.png')).default,
                'esrilightgray.png': (await import('./images/esrilightgray.png')).default,
                'cityofhattiesburg.png': (await import('./images/cityofhattiesburg.png')).default,
            };

            for (const bmDef of basemapConfig) {
                const layers = (bmDef.layers || []).map((layerDef: any) => {
                    return new TileLayer({ url: layerDef.url });
                });

                // Resolve thumbnail URL
                let thumbnailUrl = bmDef.thumbnailUrl;
                if (thumbnailUrl && thumbnailUrl.startsWith('images/')) {
                    const imageName = thumbnailUrl.replace('images/', '');
                    thumbnailUrl = thumbnailMap[imageName] || bmDef.thumbnailUrl;
                }

                customBasemaps.push(new Basemap({
                    baseLayers: layers,
                    title: bmDef.title,
                    thumbnailUrl: thumbnailUrl
                }));
            }

            const webmapBasemapTitle = (config as any).basemapGallery?.webmapBasemapTitle;
            if (webmapBasemapTitle && webmap.basemap) {
                customBasemaps.push(new Basemap({
                    baseLayers: webmap.basemap.baseLayers.toArray(),
                    title: webmapBasemapTitle,
                    thumbnailUrl: thumbnailMap['cityofhattiesburg.png']
                }));
            }

            const basemapGallery = new BasemapGallery({
                view,
                source: customBasemaps
            });
            const print = new Print({ view, printServiceUrl: config.exportWebMapService });

            const scaleBar = new ScaleBar({ view, unit: "dual", style: "ruler" });
            view.ui.add(scaleBar, { position: "bottom-left", index: 0 });

            view.ui.components = ["attribution"];

            const topLeftRow = document.createElement("div");
            topLeftRow.className = "custom-top-left-row";
            Object.assign(topLeftRow.style, {
                display: "flex",
                gap: "8px",
            });
            view.ui.add(topLeftRow, { position: "top-left", index: 0 });

            const zoomNode = document.createElement("div");
            const searchWrap = document.createElement("div");
            Object.assign(searchWrap.style, { minWidth: "280px", maxWidth: "360px" });
            const searchNode = document.createElement("div");

            topLeftRow.appendChild(zoomNode);
            searchWrap.appendChild(searchNode);
            topLeftRow.appendChild(searchWrap);

            const zoom = new Zoom({ view, container: zoomNode });
            const search = new Search({
                view,
                container: searchNode,
                includeDefaultSources: true,
                popupEnabled: false,
                allPlaceholder: "ArcGIS World Geocoder",
                locationEnabled: false
            });

            const home = new Home({ view });
            view.ui.add(home, { position: "top-left", index: 1 });

            const gsv = googleStreetView(view, {
                iconUrl: streetViewIcon,
                startAtCenter: true,
                resizable: true
            });

            const draw = drawWidget(view);

            const isMobile = window.innerWidth <= 768;
            const widgetPlacement = isMobile ? "right" : "bottom";

            const drawExpand = new Expand({
                view,
                content: draw.element as any,
                group: "top-right-tools",
                expandTooltip: "Draw",
                expandIcon: "annotate-tool",
                collapseIcon: "annotate-tool",
                placement: "bottom",
                container: wrap(),
                mode: "floating"
            });

            // keep visibility in sync
            draw.setVisible(drawExpand.expanded);
            drawExpand.watch("expanded", (v: boolean) => draw.setVisible(!!v));

            const gsvExpand = new Expand({
                view,
                content: gsv.element as any,
                expandTooltip: "Street View",
                expanded: false,
                expandIcon: "person-view",
                collapseIcon: "person-view",
                mode: "floating"
            });
            view.ui.add(gsvExpand, { position: "top-left", index: 2 });
            gsv.setVisible(gsvExpand.expanded);
            gsvExpand.watch("expanded", (v) => gsv.setVisible(!!v));

            const measurementDiv = document.createElement("div");
            measurementDiv.style.minWidth = "fit-content";
            measurementDiv.style.minHeight = "fit-content";

            const measurement = new Measurement({
                view,
                container: measurementDiv,
                linearUnit: "feet",
                areaUnit: "acres",
            });

            const mBar = document.createElement("div");
            mBar.style.cssText = "display: flex; gap: 8px; margin: 8px; align-items: center;";

            const btnDist = document.createElement("button");
            btnDist.className = "esri-button";
            btnDist.title = "Distance";
            btnDist.setAttribute("aria-label", "Distance");
            btnDist.innerHTML = `<span class="esri-icon esri-icon-measure-line" aria-hidden="true"></span>`;

            const btnArea = document.createElement("button");
            btnArea.className = "esri-button";
            btnArea.title = "Area";
            btnArea.setAttribute("aria-label", "Area");
            btnArea.innerHTML = `<span class="esri-icon esri-icon-measure-area" aria-hidden="true"></span>`;

            const btnClear = document.createElement("button");
            btnClear.className = "esri-button";
            btnClear.title = "Clear";
            btnClear.setAttribute("aria-label", "Clear");
            btnClear.innerHTML = `<span class="esri-icon esri-icon-trash" aria-hidden="true"></span>`;

            mBar.append(btnDist, btnArea, btnClear);
            styleEl.textContent += `
                .esri-button { display:inline-grid; place-items:center; width:32px; height:32px; padding:0; }
                .esri-button .esri-icon { font-size:16px; line-height:1; }
                .custom-top-right-row .esri-button,
                .custom-top-right-row .esri-button:hover,
                .custom-top-right-row .esri-button:focus,
                .custom-top-right-row .esri-button:active {
                  color: #2b2b2b !important;
                  background: #fff !important;
                  border: 1px solid #dcdcdc !important;
                }

                .custom-top-right-row .esri-button:hover { background: #f5f5f5 !important; }
                .custom-top-right-row .esri-button[aria-pressed="true"] {
                  background: #e6f2fb !important;
                  color: #005e95 !important;
                  border-color: #9bc8e5 !important;
                }

                .custom-top-right-row .esri-button .esri-icon { font-size: 16px; line-height: 1; }
            `;

            const measureWrap = document.createElement("div");
            measureWrap.append(mBar, measurementDiv);

            const measureExpand = new Expand({
                view,
                content: measureWrap,
                group: "top-right-tools",
                expandTooltip: "Measure",
                expandIcon: "measure",
                collapseIcon: "measure",
                placement: widgetPlacement,
                container: wrap(),
                mode: "floating"
            });
            const legendExpand = new Expand({ view, content: legend, group: "top-right-tools", expandTooltip: "Legend", collapseIcon: "legend", placement: widgetPlacement, container: wrap(), mode: "floating" });
            const llExpand = new Expand({ view, content: layerList, group: "top-right-tools", expandTooltip: "Layers", collapseIcon: "layers", placement: widgetPlacement, container: wrap(), mode: "floating" });
            const bgExpand = new Expand({ view, content: basemapGallery, group: "top-right-tools", expandTooltip: "Basemaps", collapseIcon: "basemap", placement: widgetPlacement, container: wrap(), mode: "floating" });

            function setTool(tool: "distance" | "area" | null) {
                measurement.clear();
                (measurement as any).activeTool = tool;
            }

            const m = measurement as any;

            function startOrToggle(tool: "distance" | "area") {
                if (m.activeTool === tool) {
                    m.clear();
                    m.activeTool = null;
                    return;
                }

                m.clear();
                m.activeTool = null;
                requestAnimationFrame(() => {
                    m.activeTool = tool;
                });
            }

            btnDist.onclick = () => startOrToggle("distance");
            btnArea.onclick = () => startOrToggle("area");
            btnClear.onclick = () => {
                m.clear();
                m.activeTool = null;
            };

            m.watch("activeTool", (tool: "distance" | "area" | null) => {
                btnDist.setAttribute("aria-pressed", String(tool === "distance"));
                btnArea.setAttribute("aria-pressed", String(tool === "area"));
                (view as any).__measuring = !!tool;
            });

            (measurement as any).watch("activeTool", (tool: "distance" | "area" | null) => {
                (view as any).__measuring = !!tool;
            });

            measureExpand.watch("expanded", (open: boolean) => {
                if (open) {
                    requestAnimationFrame(() => m.renderNow?.());
                } else {
                    m.clear();
                    m.activeTool = null;
                    btnDist.setAttribute("aria-pressed", "false");
                    btnArea.setAttribute("aria-pressed", "false");
                }
            });

            const printExpand = new Expand({ view, content: print, group: "top-right-tools", expandTooltip: "Print", collapseIcon: "print", placement: widgetPlacement, container: wrap(), mode: "floating" });

            // Fix calcite-action button flex property to center icons
            function fixCalciteActionButtons() {
                const calciteActions = document.querySelectorAll('calcite-action');
                calciteActions.forEach((action) => {
                    const shadowRoot = action.shadowRoot;
                    if (shadowRoot) {
                        const button = shadowRoot.querySelector('.button');
                        if (button && !(button as any).__flexFixed) {
                            (button as HTMLElement).style.flex = 'none';
                            (button as HTMLElement).style.justifyContent = 'center';
                            (button as any).__flexFixed = true;
                        }
                    }
                });
            }

            // Run immediately and after delays to catch dynamically added elements
            setTimeout(fixCalciteActionButtons, 100);
            setTimeout(fixCalciteActionButtons, 500);
            setTimeout(fixCalciteActionButtons, 1000);

            // Watch for new calcite-action elements being added
            const observer = new MutationObserver(() => {
                fixCalciteActionButtons();
            });
            observer.observe(document.body, { childList: true, subtree: true });

            const coord = installCompactCoordinate(view, {
                decimals: 6,
                pinImageUrl: require("./custom-widgets/images/esriGreenPin16x26.png") as string,
                attachToUi: false
            });

            view.ui.add(coord.element as any, "manual");

            const pillEl = coord.element as HTMLElement;
            Object.assign(pillEl.style, { position: "absolute", left: "8px", bottom: "70px", zIndex: "5" });

            const vc = view.container as HTMLElement | null;
            const sbEl = vc ? (vc.querySelector(".esri-scale-bar") as HTMLElement | null) : null;

            function placeAboveScale() {
                if (!vc || !sbEl) return;
                const cr = vc.getBoundingClientRect();
                const sr = sbEl.getBoundingClientRect();
                const bottomDistance = Math.max(0, cr.bottom - sr.top);
                const gap = 12;
                pillEl.style.bottom = `${bottomDistance + gap}px`;
            }

            placeAboveScale();
            const ro1 = new ResizeObserver(placeAboveScale);
            const ro2 = new ResizeObserver(placeAboveScale);
            if (sbEl) ro1.observe(sbEl);
            if (vc) ro2.observe(vc);
            window.addEventListener("resize", placeAboveScale);
            const uiHandle = view.watch("size", placeAboveScale);

            (view as any).__widgetCleanup = () => {
                try { view.ui.remove(gsvExpand); } catch { }
                gsvExpand.destroy();
                gsv.destroy();

                printExpand.destroy(); legendExpand.destroy(); llExpand.destroy(); bgExpand.destroy();
                measureExpand.destroy(); measurement.destroy();
                print.destroy(); legend.destroy(); layerList.destroy(); basemapGallery.destroy();

                try { ro1.disconnect(); ro2.disconnect(); } catch { }
                uiHandle.remove();
                coord.destroy();

                scaleBar.destroy();
                styleEl.remove();
            };
        }, (err: any) => {
            if (err?.name === "AbortError" || err?.message === "Aborted") return;
            console.error("[ArcGISMap] view.when error", err);
        });

        return () => {
            const v = viewRef.current as any;
            if (v && typeof v.__widgetCleanup === "function") v.__widgetCleanup();
            view.destroy();
        };
    }, [itemId]);

    useImperativeHandle(ref, () => ({
        zoomTo({ latitude, longitude }) {
            viewRef.current?.goTo({ center: [longitude, latitude], zoom: 18 }).catch(() => { });
        },
        highlightAndZoom(geomJson?: any, center?: { latitude: number; longitude: number }) {
            const view = viewRef.current;
            const hl = highlightLayerRef.current;
            if (!view || !hl) return;
            hl.removeAll();
            if (geomJson) {
                const geometry = geometryJsonUtils.fromJSON(geomJson);
                const symbol = new SimpleFillSymbol({
                    color: new Color([0, 121, 193, 0.2]),
                    outline: new SimpleLineSymbol({ color: new Color([0, 121, 193, 1]), width: 2 }),
                });
                const g = new Graphic({ geometry, symbol });
                hl.add(g);
                view.goTo({ target: geometry, zoom: 18 }).catch(() => { });
                return;
            }
            if (center) {
                view.goTo({ center: [center.longitude, center.latitude], zoom: 18 }).catch(() => { });
            }
        },
    }));

    useEffect(() => {
        if (!viewRef.current) return;
        const view = viewRef.current;

        const openParcelPopup = (feature: __esri.Graphic, location: __esri.Point) => {
            if (feature.attributes?.PPIN != null) {
                feature.attributes.PPIN = Math.round(feature.attributes.PPIN);
            }
            feature.popupTemplate = new PopupTemplate({
                title: "{Address}",
                content: [
                    {
                        type: "fields",
                        fieldInfos: [
                            { fieldName: "County", label: "County" },
                            { fieldName: "PropertyOwner", label: "Owner" },
                            { fieldName: "Address", label: "Address" },
                            { fieldName: "PPIN", label: "PPIN" },
                        ],
                    },
                ],
            });
            view.openPopup({ features: [feature], location });
        };

        const isParcelOrBuilding = (layer: any): { ok: boolean; id?: number; title?: string } => {
            try {
                const title = (layer?.title ?? "").toString().toLowerCase();
                const url: string = String(layer?.url ?? "");
                const idPart = url.split("/").filter(Boolean).pop();
                const parsedId = Number(idPart);

                // Check parent layer title if this layer is in a group
                const parentTitle = (layer?.parent?.title ?? "").toString().toLowerCase();

                // Check if this layer URL matches configured parcel or geometry layers
                let isConfiguredParcelLayer = false;
                if (config.geometryLayer && config.geometryLayer.trim()) {
                    const geometryLayerUrl = config.geometryLayer;
                    isConfiguredParcelLayer = url === geometryLayerUrl || url.startsWith(geometryLayerUrl.replace(/\/\d+$/, ''));
                }
                if (!isConfiguredParcelLayer && config.parcelLayer && config.parcelLayer.trim()) {
                    const parcelLayerUrl = config.parcelLayer;
                    isConfiguredParcelLayer = url === parcelLayerUrl || url.startsWith(parcelLayerUrl.replace(/\/\d+$/, ''));
                }

                const isParcel = isConfiguredParcelLayer ||
                    title.includes("parcel") ||
                    title.includes("cadastral") ||
                    parentTitle.includes("parcel") ||
                    parentTitle.includes("cadastral");
                const isBuilding = title.includes("building") || parentTitle.includes("building");

                return { ok: isParcel || isBuilding, id: Number.isFinite(parsedId) ? parsedId : undefined, title: layer?.title };
            } catch (err) {
                console.error("Error in isParcelOrBuilding:", err);
                return { ok: false };
            }
        };

        let activeAbort: AbortController | null = null;

        const onClick = async (evt: __esri.ViewClickEvent) => {
            const v: any = view;
            if (!view || v.__measuring || v.__drawing) return;

            const mp = evt.mapPoint;
            if (!mp) return;

            if (activeAbort) activeAbort.abort();
            activeAbort = new AbortController();
            const signal = activeAbort.signal;

            const center = toLatLong(mp);

            let ht;
            try {
                ht = await view.hitTest(evt);
            } catch (err: any) {
                if (err?.name === "AbortError") return;
                ht = null;
            }
            if (signal.aborted) return;

            if (!ht || !ht.results?.length) {
                // Fallback: parcels rendered as tiles — query REST directly
                try {
                    const parcelUrl = config.parcelLayer;

                    const queryObj = new Query();
                    queryObj.geometry = mp;
                    queryObj.spatialRelationship = "intersects";
                    queryObj.returnGeometry = true;
                    queryObj.outFields = ["*"];
                    queryObj.num = 1;

                    const response = await queryModule.executeQueryJSON(parcelUrl, queryObj);
                    if (signal.aborted) return;

                    if (response.features && response.features.length > 0) {
                        const feature = response.features[0];
                        const geomJson = feature.geometry?.toJSON?.();
                        const attrs = feature.attributes ?? {};

                        let finalCenter = center;
                        try {
                            const buildingUrl = config.parcelLayer.replace(/\/\d+$/, "/28");
                            const buildingQuery = new Query();
                            buildingQuery.geometry = feature.geometry;
                            buildingQuery.spatialRelationship = "intersects";
                            buildingQuery.returnGeometry = true;
                            buildingQuery.outFields = ["*"];

                            const buildingResponse = await queryModule.executeQueryJSON(buildingUrl, buildingQuery);
                            if (signal.aborted) return;

                            if (buildingResponse.features && buildingResponse.features.length > 0) {
                                let largestBuilding = buildingResponse.features[0];
                                let largestArea = 0;

                                for (const bldg of buildingResponse.features) {
                                    const geom = bldg.geometry;
                                    if (geom && (geom as any).extent) {
                                        const extent = (geom as any).extent;
                                        const area = (extent.xmax - extent.xmin) * (extent.ymax - extent.ymin);
                                        if (area > largestArea) {
                                            largestArea = area;
                                            largestBuilding = bldg;
                                        }
                                    }
                                }

                                const buildingGeom = largestBuilding.geometry;
                                if (buildingGeom) {
                                    let centroidPoint;
                                    if ((buildingGeom as any).centroid) {
                                        centroidPoint = (buildingGeom as any).centroid;
                                    } else if ((buildingGeom as any).extent) {
                                        const extent = (buildingGeom as any).extent;
                                        centroidPoint = {
                                            x: (extent.xmin + extent.xmax) / 2,
                                            y: (extent.ymin + extent.ymax) / 2,
                                            spatialReference: extent.spatialReference
                                        };
                                    }
                                    if (centroidPoint) finalCenter = toLatLong(centroidPoint as __esri.Point);
                                }
                            }
                        } catch (err: any) {
                            if (err?.name !== "AbortError") console.error("Building query error:", err);
                        }

                        onMapIdentify?.({ center: finalCenter, geometryJson: geomJson, attributes: attrs, layerId: 29, layerTitle: "Parcels" });
                        openParcelPopup(feature, mp);
                        try { (ref as any)?.current?.highlightAndZoom?.(geomJson, finalCenter); } catch { }
                        return;
                    }
                } catch (err: any) {
                    if (err?.name !== "AbortError") console.error("Parcel query error:", err);
                }
                return;
            }

            const wanted = ht.results.find(r => {
                const layer = (r as any)?.graphic?.layer;
                const result = layer && isParcelOrBuilding(layer);
                return result && result.ok;
            });

            if (!wanted) {
                // Hit test found results but none were parcel/building — fall back to direct parcel query
                try {
                    const parcelUrl = config.parcelLayer;
                    const queryObj = new Query();
                    queryObj.geometry = mp;
                    queryObj.spatialRelationship = "intersects";
                    queryObj.returnGeometry = true;
                    queryObj.outFields = ["*"];
                    queryObj.num = 1;
                    const response = await queryModule.executeQueryJSON(parcelUrl, queryObj);
                    if (signal.aborted) return;
                    if (response.features && response.features.length > 0) {
                        const feature = response.features[0];
                        const geomJson = feature.geometry?.toJSON?.();
                        onMapIdentify?.({ center, geometryJson: geomJson, attributes: feature.attributes ?? {}, layerId: 29, layerTitle: "Parcels" });
                        openParcelPopup(feature, mp);
                        try { (ref as any)?.current?.highlightAndZoom?.(geomJson, center); } catch { }
                    }
                } catch (err) {
                    console.error("Parcel query error:", err);
                }
                return;
            }

            const g: __esri.Graphic | undefined = (wanted as any)?.graphic;
            if (!g) return;

            const isBuilding = (g as any).layer?.title?.toLowerCase().includes("building");
            if (isBuilding) {
                try {
                    const parcelUrl = config.parcelLayer;
                    const queryObj = new Query();
                    queryObj.geometry = mp;
                    queryObj.spatialRelationship = "intersects";
                    queryObj.returnGeometry = true;
                    queryObj.outFields = ["*"];
                    queryObj.num = 1;

                    const response = await queryModule.executeQueryJSON(parcelUrl, queryObj);
                    if (signal.aborted) return;

                    if (response.features && response.features.length > 0) {
                        const parcelFeature = response.features[0];
                        const geomJson = parcelFeature.geometry?.toJSON?.();

                        g.popupTemplate = new PopupTemplate({
                            title: "{FullAddr}",
                            content: [{ type: "fields", fieldInfos: [
                                { fieldName: "FullAddr", label: "Address" },
                                { fieldName: "Municipality", label: "Municipality" },
                                { fieldName: "AddrType", label: "Type" },
                                { fieldName: "AddrSubType", label: "Subtype" },
                                { fieldName: "Units", label: "Units" },
                                { fieldName: "Floors", label: "Floors" },
                            ]}],
                        });
                        if (parcelFeature.attributes?.PPIN != null) {
                            parcelFeature.attributes.PPIN = Math.round(parcelFeature.attributes.PPIN);
                        }
                        parcelFeature.popupTemplate = new PopupTemplate({
                            title: "{Address}",
                            content: [{ type: "fields", fieldInfos: [
                                { fieldName: "County", label: "County" },
                                { fieldName: "PropertyOwner", label: "Owner" },
                                { fieldName: "Address", label: "Address" },
                                { fieldName: "PPIN", label: "PPIN" },
                            ]}],
                        });

                        onMapIdentify?.({ center, geometryJson: geomJson, attributes: parcelFeature.attributes ?? {}, layerId: 29, layerTitle: "Parcels" });
                        view.openPopup({ features: [g, parcelFeature], location: mp });
                        try { (ref as any)?.current?.highlightAndZoom?.(geomJson, center); } catch { }
                        return;
                    }
                } catch (err: any) {
                    if (err?.name !== "AbortError") console.error("Building->parcel query error:", err);
                }
                return;
            }

            const geomJson = g.geometry?.toJSON?.();
            const attrs = g.attributes ?? {};
            const layerInfo = isParcelOrBuilding((g as any).layer);

            onMapIdentify?.({ center, geometryJson: geomJson, attributes: attrs, layerId: layerInfo.id, layerTitle: layerInfo.title });
            view.openPopup({ features: [g], location: mp });
            try { (ref as any)?.current?.highlightAndZoom?.(geomJson, center); } catch { }
        };

        const handle = view.on("click", onClick);
        return () => { handle.remove(); activeAbort?.abort(); };
    }, [itemId, onMapIdentify]);

    return <div style={MapDivStyle} ref={mapDiv} />;
});

export default ArcGISMap;

function toLatLong(p: __esri.Point): { latitude: number; longitude: number } {
    const g = (p.spatialReference?.isWGS84 || p.spatialReference?.isGeographic)
        ? p
        : (webMercatorUtils.webMercatorToGeographic(p) as Point);

    return { latitude: g.y as number, longitude: g.x as number };
}
